import React, { useState } from 'react';
import logo from '../../../assets/images/logo.jpeg';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../../../services/AuthService';

const PasswordReset = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const tempPassword = new URLSearchParams(location.search).get('temp');
  const email = new URLSearchParams(location.search).get('email');

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasNumbers) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    // Validate temporary password
    if (currentPassword !== tempPassword) {
      setErrorMessage("Current password is incorrect");
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage("New passwords do not match");
      return;
    }

    // Validate new password is different from temporary password
    if (newPassword === tempPassword) {
      setErrorMessage("New password must be different from the temporary password");
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      return;
    }

    try {
      const response = await AuthService.resetPassword(email, tempPassword, newPassword);
      
      if (response.success) {
        setSuccessMessage('Password successfully reset. Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setErrorMessage(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setErrorMessage('Failed to reset password. Please try again.');
    }
  };

  const handleChange = (e, field) => {
    if (errorMessage) setErrorMessage(null);
    switch(field) {
      case 'currentPassword':
        setCurrentPassword(e.target.value);
        break;
      case 'newPassword':
        setNewPassword(e.target.value);
        break;
      case 'confirmPassword':
        setConfirmPassword(e.target.value);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img alt="Your Company" src={logo} className="mx-auto h-10 w-auto" />
          <h2 className="mt-10 text-center text-2xl font-bold tracking-tight text-gray-900">
            Reset Your Password
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form onSubmit={handlePasswordReset}>
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-900">
                Temporary Password
              </label>
              <div className="mt-2">
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => handleChange(e, 'currentPassword')}
                  id="currentPassword"
                  name="currentPassword"
                  required
                  autoComplete="current-password"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:outline-[#0D6EFD] sm:text-sm"
                />
              </div>
            </div>

            <div>
              <br />
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-900">
                New Password
              </label>
              <div className="mt-2">
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => handleChange(e, 'newPassword')}
                  id="newPassword"
                  name="newPassword"
                  required
                  autoComplete="new-password"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:outline-[#0D6EFD] sm:text-sm"
                />
              </div>
            </div>

            <div>
              <br />
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
                Confirm New Password
              </label>
              <div className="mt-2">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => handleChange(e, 'confirmPassword')}
                  id="confirmPassword"
                  name="confirmPassword"
                  required
                  autoComplete="new-password"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:outline-[#0D6EFD] sm:text-sm"
                />
              </div>
            </div>

            {/* Password Requirements */}
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700">Password Requirements:</p>
              <ul className="mt-2 text-sm text-gray-600 list-disc list-inside">
                <li>At least 8 characters long</li>
                <li>At least one uppercase letter</li>
                <li>At least one lowercase letter</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>

            {/* Display error message if there's one */}
            {errorMessage && (
              <div className="mt-4 text-center text-sm text-red-500">
                {errorMessage}
              </div>
            )}

            {/* Display success message if there's one */}
            {successMessage && (
              <div className="mt-4 text-center text-sm text-green-500">
                {successMessage}
              </div>
            )}

            <div>
              <br />
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-[#0D6EFD] px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0a58ca] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0D6EFD]"
              >
                Reset Password
              </button>
            </div>
          </form>

          <p className="mt-10 text-center text-sm text-gray-500">
            Remember your password?{' '}
            <a href="/login" className="font-semibold text-[#0D6EFD] hover:text-[#0a58ca]">
              Sign in here
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default PasswordReset; 